import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import {
  useGetAdminChatConversations,
  useGetAdminChatConversationsConversationId,
} from "@/lib/api/generated/chat/chat";
import { ConversationReadingPane } from "@/inbox/ConversationReadingPane";
import { IncomingInbox } from "@/inbox/IncomingInbox";
import { cn } from "@rumbledirect/ui-v2/cn";

interface IncomingSearch {
  /** The selected conversation, so a link/refresh reopens the same thread. */
  readonly conversationId?: string;
}

export const Route = createFileRoute("/_authenticated/inbox")({
  // The selected conversation lives in the URL so a shared link or a reload
  // reopens the same thread, mirroring the pattern used by `tasks`/`dossiers`.
  validateSearch: (search: Record<string, unknown>): IncomingSearch => ({
    conversationId:
      typeof search.conversationId === "string"
        ? search.conversationId
        : undefined,
  }),
  component: IncomingPage,
});

/**
 * Incoming — the WhatsApp inbox. Two-column layout: `IncomingInbox` owns the
 * filterable conversation list, `ConversationReadingPane` shows the selected
 * conversation's thread. List and detail are fetched independently (list
 * endpoint returns summaries only; the thread comes from its own detail
 * endpoint), matching the API's shape.
 *
 * This first slice fetches the full first page unfiltered and lets
 * `IncomingInbox`'s `inboxQueue` selectors filter/sort client-side — the same
 * approach the prototype used. Server-side filter params
 * (`ChatConversationListParams`) exist and can be wired in a later slice if
 * the inbox grows past a single page.
 */
function IncomingPage(): ReactNode {
  const { conversationId } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Owned here rather than in the pane because the route is what survives a
  // conversation switch, and the form must not stay open across threads.
  const [createOpen, setCreateOpen] = useState(false);

  // The form is about the conversation it was opened from, so selecting
  // another thread closes it: leaving it open would present half-typed details
  // from the previous e-mail as if they belonged to the new one. Derived
  // during render rather than in an effect, so the new thread never paints
  // with the old form open.
  const [openedForId, setOpenedForId] = useState(conversationId);
  if (openedForId !== conversationId) {
    setOpenedForId(conversationId);
    setCreateOpen(false);
  }

  const { data, isPending, isError } = useGetAdminChatConversations({});
  const conversations = data?.member ?? [];

  const detailQuery = useGetAdminChatConversationsConversationId(
    conversationId ?? "",
    { query: { enabled: conversationId !== undefined } },
  );

  const selectConversation = (id: string): void => {
    void navigate({ search: { conversationId: id } });
  };

  const showDetailOnMobile = conversationId !== undefined;

  return (
    <div className="flex h-full min-h-0">
      <div
        className={cn(
          "flex min-h-0 w-full flex-col border-border/70 lg:shrink-0 lg:border-r",
          // Constant width: resizing the list on open would shift the reading
          // pane sideways, which is the movement the rail layout exists to
          // prevent. At xl there is room for a roomier list.
          "lg:w-[300px] xl:w-[360px]",
          showDetailOnMobile ? "hidden lg:flex" : "flex",
        )}
      >
        {isError ? (
          <div className="grid flex-1 place-items-center px-6 py-16 text-center text-sm text-muted-foreground">
            We konden de berichten niet laden.
          </div>
        ) : (
          <IncomingInbox
            conversations={isPending ? [] : conversations}
            selectedId={conversationId}
            onSelect={selectConversation}
            isLoading={isPending}
          />
        )}
      </div>

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto bg-surface-muted/30",
          showDetailOnMobile ? "block" : "hidden lg:block",
        )}
      >
        <ConversationReadingPane
          conversation={detailQuery.data}
          isLoading={conversationId !== undefined && detailQuery.isPending}
          isError={detailQuery.isError}
          createOpen={createOpen}
          onCreateOpenChange={setCreateOpen}
        />
      </div>
    </div>
  );
}
